/**
 * CartProvider saga
 */

import { call, put, takeLatest } from 'redux-saga/effects';
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import toast from '_platform/src/utils/toast';
import history from '_platform/src/utils/history';
import { api, apiErrorHandler } from '_platform/src/utils/request';
import {
  CART_CHECK_AND_CONTINUE,
  CART_DELETE_ITEM_REQUEST,
  CART_UPDATE_REWARDS_REQUEST,
} from './constants';
import { cartCheckAndContinueComplete } from './actions';
import { cartRequest } from '../WebApp/actions';

/**
 * Request cart check and continue
 *
 * @param {object} values Contains `cartRewardId`
 */
export function* requestCartCheckAndContinue(values) {
  try {
    yield put(showLoading());

    if (values.currentStep === 'cart') {
      // Update cart values
      yield call(() =>
        api.put('/Cart/v1/UpdateRewards', values.values, {
          description: 'Update Cart Rewards',
        })
      );
    }

    if (values.currentStep === 'delivery') {
      yield call(() =>
        api.put('/Cart/v1/Update', values.values, {
          description: 'Update Cart',
        })
      );
    }

    if (values.currentStep === 'confirm') {
      yield call(() =>
        api.put('/Cart/v1/Update', values.values, {
          description: 'Update Cart',
        })
      );
    }

    const response = yield call(() =>
      api.get('/Cart/v1/CanCheckout', {
        description: 'Check cart',
      })
    );

    if (values.currentStep === 'confirm') {
      // Place order
      yield call(() =>
        api.post('/Cart/v1/Order', null, { description: 'Place order' })
      );
    }

    // TODO: Check if the endpoint has stripped out any rewards and report this to user requiring them to click checkout again?

    // Update cart state
    yield put(cartRequest(true));

    if (response.data === false) {
      // Show the user an error message
      yield put(
        cartCheckAndContinueComplete(
          'Could not proceed to checkout, please try again later or contact us.'
        )
      );
    } else {
      // Redirect to next page
      yield call(history.push, {
        pathname: values.nextPagePath,
      });

      // Clear the state after location change (so the <Prompt /> is not triggered)
      yield put(cartCheckAndContinueComplete());
    }
  } catch (err) {
    yield call(toast, 'error', 'There was a problem verifying the cart');

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(`Dev: Cart CanCheckout '${apiErrorHandler(err).message}'`);
    }
  } finally {
    yield put(hideLoading());
  }
}

/**
 * Request cart item delete
 *
 * @param {object} values Contains `cartRewardId`
 */
export function* requestCartItemDelete(values) {
  try {
    yield put(showLoading());

    // eslint-disable-next-line no-unused-vars
    const response = yield call(() =>
      api.delete(`/Cart/v1/Remove/Id/${values.cartRewardId}`, null, {
        description: 'Delete cart item',
      })
    );

    // Refresh the cart
    yield put(cartRequest(true));
  } catch (err) {
    yield call(
      toast,
      'error',
      'There was a problem removing the reward from cart'
    );

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(`Dev: Cart remove item '${apiErrorHandler(err).message}'`);
    }
  } finally {
    yield put(hideLoading());
  }
}

/**
 * Request cart update
 *
 * @param {object} values Form values deltas merged into an object - this is handled by react-final-form initialValues
 */
export function* requestCartUpdateRewards(values) {
  try {
    yield put(showLoading());

    // eslint-disable-next-line no-unused-vars
    const response = yield call(() =>
      api.put('/Cart/v1/UpdateRewards', values.values, {
        description: 'Update Rewards',
      })
    );

    // Refresh the cart
    yield put(cartRequest(true));
  } catch (err) {
    yield call(toast, 'error', 'There was a problem updating the cart');

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(`Dev: Update rewards '${apiErrorHandler(err).message}'`);
    }
  } finally {
    yield put(hideLoading());
  }
}

/**
 * Default saga managers the watcher lifecycle
 */
export default function* defaultSaga() {
  // Specify the constant to watch and the saga generator function to call when one is triggered
  // It returns a task descriptor (just like a fork) so we can continue execution, and will be
  // automatically cancelled on unmount (unless the mode is set to DAEMON)
  // Specify as many yields (watchers) here as necessary
  yield takeLatest(CART_CHECK_AND_CONTINUE, requestCartCheckAndContinue);
  yield takeLatest(CART_DELETE_ITEM_REQUEST, requestCartItemDelete);
  yield takeLatest(CART_UPDATE_REWARDS_REQUEST, requestCartUpdateRewards);
}
